'use client'

import * as React from 'react'
import { Check, Copy, Facebook, Linkedin, Printer, Twitter } from 'lucide-react'

// Social share + AI summary bar, shown below the author row on every blog
// post. Replaces the old floating PrintArticleButton.
//
// AI summary links open the article URL in the respective assistant with a
// pre-filled prompt so the user gets a useful summary, not a blank page.

type Tone = 'dark' | 'light'

const AI_ASSISTANTS = [
  {
    id: 'chatgpt',
    label: 'GPT',
    title: 'Open in ChatGPT',
    href: (url: string, title: string) =>
      `https://chatgpt.com/?q=${encodeURIComponent(`Summarise this article and give me 3 actionable takeaways: "${title}" — ${url}`)}`,
    color: '#10a37f',
  },
  {
    id: 'gemini',
    label: 'Gemini',
    title: 'Open in Gemini',
    href: (url: string, title: string) =>
      `https://gemini.google.com/app?q=${encodeURIComponent(`Summarise: "${title}" ${url}`)}`,
    color: '#4285f4',
  },
  {
    id: 'perplexity',
    label: 'Perplexity',
    title: 'Open in Perplexity',
    href: (url: string, title: string) =>
      `https://www.perplexity.ai/search?q=${encodeURIComponent(`${title} ${url}`)}`,
    color: '#20b2aa',
  },
  {
    id: 'claude',
    label: 'Claude',
    title: 'Open in Claude',
    href: (url: string, title: string) =>
      `https://claude.ai/new?q=${encodeURIComponent(`Summarise this article and give me 3 actionable takeaways: "${title}" — ${url}`)}`,
    color: '#d97757',
  },
  {
    id: 'grok',
    label: 'Grok',
    title: 'Open in Grok',
    href: (url: string, title: string) =>
      `https://x.com/i/grok?text=${encodeURIComponent(`Summarise this article: "${title}" ${url}`)}`,
    color: '#000000',
  },
]

export function ArticleShareBar({
  title,
  slug,
  tone = 'dark',
}: {
  title: string
  slug: string
  tone?: Tone
}) {
  const [copied, setCopied] = React.useState(false)

  const url = `https://idukki.io/blog/${slug}`
  const encodedUrl = encodeURIComponent(url)
  const encodedTitle = encodeURIComponent(title)

  const isDark = tone === 'dark'
  const btnBase = isDark
    ? 'flex items-center justify-center rounded-full bg-white/10 text-white/80 transition hover:bg-white/20 hover:text-white'
    : 'flex items-center justify-center rounded-full border border-border bg-background text-foreground/60 transition hover:border-primary/40 hover:text-foreground'

  function handleCopy() {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  function handlePrint() {
    window.print()
  }

  const labelCls = isDark ? 'text-white/45' : 'text-muted-foreground'

  return (
    <div className="mt-5 space-y-2.5">
      {/* Row 1: social share */}
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-[11px] font-semibold uppercase tracking-[0.1em] ${labelCls} mr-1`}>
          Share
        </span>

        {/* Copy link */}
        <button
          type="button"
          onClick={handleCopy}
          title="Copy link"
          className={`${btnBase} h-8 w-8`}
          aria-label="Copy article link"
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        </button>

        {/* LinkedIn */}
        <a
          href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
          target="_blank"
          rel="noopener noreferrer"
          title="Share on LinkedIn"
          aria-label="Share on LinkedIn"
          className={`${btnBase} h-8 w-8`}
        >
          <Linkedin className="h-3.5 w-3.5" />
        </a>

        {/* X / Twitter */}
        <a
          href={`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`}
          target="_blank"
          rel="noopener noreferrer"
          title="Share on X"
          aria-label="Share on X"
          className={`${btnBase} h-8 w-8`}
        >
          <Twitter className="h-3.5 w-3.5" />
        </a>

        {/* Facebook */}
        <a
          href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
          target="_blank"
          rel="noopener noreferrer"
          title="Share on Facebook"
          aria-label="Share on Facebook"
          className={`${btnBase} h-8 w-8`}
        >
          <Facebook className="h-3.5 w-3.5" />
        </a>

        {/* Print / PDF — manual, no floating button */}
        <button
          type="button"
          onClick={handlePrint}
          title="Print / Save as PDF"
          aria-label="Print or save as PDF"
          className={`${btnBase} h-8 w-8`}
        >
          <Printer className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Row 2: AI summary */}
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-[11px] font-semibold uppercase tracking-[0.1em] ${labelCls} mr-1`}>
          AI summary
        </span>
        {AI_ASSISTANTS.map((ai) => (
          <a
            key={ai.id}
            href={ai.href(url, title)}
            target="_blank"
            rel="noopener noreferrer"
            title={ai.title}
            className={
              isDark
                ? 'rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-semibold text-white/80 transition hover:bg-white/20 hover:text-white'
                : 'rounded-full border border-border bg-background px-2.5 py-1 text-[11px] font-semibold text-foreground/60 transition hover:border-primary/40 hover:text-foreground'
            }
          >
            {ai.label}
          </a>
        ))}
      </div>
    </div>
  )
}
